import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useGetPages } from "../../hooks/pages/useGetPages";
import { useDeletePage } from "../../hooks/pages/useDeletePage";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import PagesTableRow from "./Table/PagesTableRow";
import { PagesTableColumns } from "./Table/PagesTableColumns";
import { usePermissions } from "../../auth/PermissionsContext";

const PagesTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("cms.manage"),
            create: has("cms.manage"),
            edit: has("cms.manage"),
            delete: has("cms.manage"),
        }),
        [has]
    );

    const navigate = useNavigate();

    // Filter States
    const [statusFilter, setStatusFilter] = useState("");

    // CRUD Hooks
    const { deletePageMutate, isLoading: isDeleting } = useDeletePage();

    const {
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: "/pages",
        deleteSingle: deletePageMutate,
    });

    const params = useMemo(
        () => ({
            page,
            per_page: perPage,
            search: search || undefined,
            is_active: statusFilter !== "" ? statusFilter : undefined,
            sort_by: 'created_at',
            sort_order: order,
        }),
        [page, perPage, search, statusFilter, order]
    );

    const { isLoading, error, pages, meta } = useGetPages(params);

    // تجهيز الفلاتر لـ TableLayout
    const tableFilters = [
        {
            name: "status",
            label: { ar: "الحالة", en: "Status" },
            value: statusFilter,
            onChange: (val) => setStatusFilter(val),
            options: [
                { value: "", label: { ar: "الكل", en: "All" } },
                { value: "1", label: { ar: "نشط", en: "Active" } },
                { value: "0", label: { ar: "غير نشط", en: "Inactive" } },
            ]
        }
    ];

    const renderRow = (rowParams) => (
        <PagesTableRow
            key={rowParams.item.id}
            page={rowParams.item}
            index={rowParams.index}
            isSelected={selectedIds.includes(rowParams.item.id)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
        />
    );

    const title = isArabic ? "الصفحات" : "Pages";
    const subtitle = isArabic ? "إدارة الصفحات الثابتة" : "Manage static pages";

    return (
        <TableLayout
            title={title}
            subtitle={subtitle}
            search={search}
            onSearchChange={setSearch}
            addButtonLabel={isArabic ? "إضافة صفحة" : "Add Page"}
            onAddClick={() => navigate("/pages/create")}
            addButtonVisible={perms.create}
            isLoading={isLoading || isDeleting}
            error={error}
            items={pages}
            meta={meta}
            page={page}
            perPage={perPage}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            order={order}
            onRequestSort={handleRequestSort}
            columns={PagesTableColumns}
            renderRow={renderRow}
            menuAnchorEl={menuAnchorEl}
            openMenu={Boolean(menuAnchorEl)}
            onMenuOpen={handleMenuOpen}
            onMenuClose={handleMenuClose}
            onMenuEdit={handleEdit}
            onMenuDelete={handleMenuDelete}
            menuDisableEdit={!perms.edit}
            menuDisableDelete={!perms.delete}
            isDeletingSingle={isDeleting}
            selectedIds={selectedIds}
            onSelectAllClick={(e) => handleSelectAllClick(e, pages)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onBulkDelete={handleBulkDelete}
            bulkDeleteDisabled={!perms.delete}
            filters={tableFilters}
            onClearAllFilters={() => {
                setStatusFilter("");
            }}
            emptyLabelAr={isArabic ? "لا توجد صفحات" : "No pages"}
            emptyLabelEn={isArabic ? "لا توجد صفحات" : "No pages"}
        />
    );
};

export default PagesTable;
